import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { ArrowLeft, ArrowRight, ScanLine, UtensilsCrossed, Network, Phone, KeyRound, Loader2, QrCode } from "lucide-react";
import { Link } from "react-router-dom";

export default function Login() {
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("password");  // "password" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpStage, setOtpStage] = useState("enter"); // enter | verify
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpInfo, setOtpInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edkOpen, setEdkOpen] = useState(false);
  const [edkToken, setEdkToken] = useState("edk_demo_test_user_001");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      const dest = u.role === "super_admin" ? "/superadmin" : u.role === "admin" ? "/admin" : u.role === "canteen_admin" ? "/rfid-terminal" : u.role === "operator" ? "/pos" : u.role === "vendor" ? "/vendor" : u.role === "staff" ? "/parent" : "/parent";
      nav(dest, { replace: true });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const edkSso = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/integrations/edkonnect/sso/login", { edk_token: edkToken });
      localStorage.setItem("grubpe_token", data.token);
      setUser(data.user);
      toast.success(`Signed in via EdKonnect: ${data.user.name}`);
      nav("/parent", { replace: true });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); setEdkOpen(false); }
  };

  const otpRequest = async () => {
    if (!otpPhone.trim()) return toast.error("Enter your phone");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login-otp/request", { phone: otpPhone.trim() });
      setOtpInfo(data); setOtpStage("verify");
      if (data.dev_code) toast.info(`Dev OTP: ${data.dev_code}`, { duration: 8000 });
      else toast.success(`OTP sent to ${data.phone_masked}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const otpVerify = async () => {
    if (!otpCode.trim()) return toast.error("Enter the OTP");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login-otp/verify", { phone: otpPhone.trim(), otp: otpCode.trim() });
      localStorage.setItem("grubpe_token", data.token);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name}`);
      const u = data.user;
      const dest = u.role === "super_admin" ? "/superadmin" : u.role === "admin" ? "/admin" : u.role === "canteen_admin" ? "/rfid-terminal" : u.role === "operator" ? "/pos" : u.role === "vendor" ? "/vendor" : "/parent";
      nav(dest, { replace: true });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream warm-grain flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-ink-600 hover:text-ink" data-testid="back-home">
          <ArrowLeft size={18} /> <BrandLogo size="sm" />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-soft border border-border">
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Welcome back</h1>
            <p className="text-sm text-ink-600 mt-2">Sign in to your Grubpe account.</p>

            <div className="mt-6 inline-flex bg-cream-200 rounded-full p-1">
              <button data-testid="mode-password" onClick={() => setMode("password")}
                className={`px-4 py-1.5 rounded-full text-xs font-bold ${mode === "password" ? "bg-ink text-white" : "text-ink-700"}`}>
                Password
              </button>
              <button data-testid="mode-otp" onClick={() => setMode("otp")}
                className={`px-4 py-1.5 rounded-full text-xs font-bold ${mode === "otp" ? "bg-ink text-white" : "text-ink-700"}`}>
                Mobile OTP
              </button>
            </div>

            {mode === "password" ? (
              <form className="mt-6 space-y-4" onSubmit={submit}>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Email</label>
                  <input data-testid="login-email" type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-3 text-ink placeholder:text-ink-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="you@school.edu" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Password</label>
                  <input data-testid="login-password" type="password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-3 text-ink placeholder:text-ink-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="••••••••" />
                </div>
                <button data-testid="login-submit" disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 disabled:opacity-60">
                  {loading ? "Signing in..." : "Sign in"} <ArrowRight size={16} />
                </button>
                <div className="text-center">
                  <a href="/forgot-password" data-testid="forgot-password-link"
                    className="text-xs font-semibold text-ink-600 hover:text-brand underline-offset-2 hover:underline">
                    Forgot password?
                  </a>
                </div>
              </form>
            ) : otpStage === "enter" ? (
              <div className="mt-6 space-y-4" data-testid="otp-login-enter">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Mobile number</label>
                  <input data-testid="otp-login-phone" autoFocus value={otpPhone}
                    onChange={e => setOtpPhone(e.target.value)} placeholder="9999999999"
                    className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-3 text-ink placeholder:text-ink-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                  <div className="text-[11px] text-ink-500 mt-1">We&apos;ll send a 6-digit code via SMS.</div>
                </div>
                <button data-testid="otp-login-send" onClick={otpRequest} disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin"/> : <KeyRound size={16}/>} Send OTP
                </button>
              </div>
            ) : (
              <div className="mt-6 space-y-4" data-testid="otp-login-verify">
                <div className="bg-cream-100 rounded-2xl p-3 text-sm">
                  <div className="font-semibold text-ink">{otpInfo?.user_name}</div>
                  <div className="text-xs text-ink-500 mt-0.5">OTP sent to {otpInfo?.phone_masked}</div>
                  {otpInfo?.dev_code && <div className="text-[11px] text-amber-700 mt-1">Dev code: <span className="font-mono font-bold">{otpInfo.dev_code}</span></div>}
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">6-digit code</label>
                  <input data-testid="otp-login-code" inputMode="numeric" maxLength={6} value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g,""))} placeholder="••••••"
                    className="mt-1 w-full rounded-xl border border-border px-4 py-3 text-2xl font-bold text-center tracking-[0.4em] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                </div>
                <button data-testid="otp-login-verify-btn" onClick={otpVerify} disabled={loading || otpCode.length < 4}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand text-white font-semibold disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin"/> : <ArrowRight size={16}/>} Verify & sign in
                </button>
                <button onClick={() => { setOtpStage("enter"); setOtpCode(""); }} className="w-full text-xs text-ink-500 underline">Use a different number</button>
              </div>
            )}

            <div className="mt-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[11px] uppercase tracking-wider text-ink-500 font-bold">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <button data-testid="edk-sso-btn" onClick={() => setEdkOpen(true)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-border hover:border-ink text-ink-700 hover:text-ink font-semibold transition-colors">
              <Network size={16} /> Sign in with EdKonnect SSO
            </button>
          </div>
        </div>
      </div>

      {edkOpen && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setEdkOpen(false)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.2em]"><Network size={14} /> EdKonnect SSO</div>
            <div className="font-heading text-2xl font-bold mt-2">Sign in with EdKonnect</div>
            <p className="text-sm text-ink-600 mt-2">Stub mode accepts any token starting with <span className="font-mono bg-cream-200 px-1 py-0.5 rounded">edk_demo_</span>. In production, this token is issued by your EdKonnect ERP.</p>
            <input data-testid="edk-token-input" value={edkToken} onChange={e => setEdkToken(e.target.value)}
              className="mt-4 w-full rounded-xl border border-border px-4 py-3 font-mono focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEdkOpen(false)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="edk-sso-confirm" onClick={edkSso} disabled={loading} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold disabled:opacity-60">Continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
